import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Baby, Calculator, ArrowLeft } from "lucide-react";
import { Button } from "@/common/components/ui/button";
import { Card, CardContent } from "@/common/components/ui/card";
import { CompactCalculatorRow } from "../CompactCalculatorRow";
import { calculateSSY, formatCurrency } from "../../utils/calculations";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import ProductLayout from "@/common/components/ProductLayout";

export const SSYCalculator = () => {
  const navigate = useNavigate();
  const [ssyAmount, setSsyAmount] = useState(50000);
  const [ssyAge, setSsyAge] = useState(1);
  const ssyRate = 8.2;

  const ssyResult = useMemo(
    () => calculateSSY(ssyAmount, ssyRate),
    [ssyAmount]
  );

  const ssyPieData = [
    { name: "Invested", value: ssyResult.invested, color: "#14b8a6" },
    { name: "Interest", value: ssyResult.interest, color: "#f59e0b" },
  ];

  return (
    <ProductLayout>
      <div className="min-h-screen bg-background py-8">
        <div className="container mx-auto px-4 md:px-6">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/calculators")}
              className="mb-4 pl-0 hover:bg-transparent hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Calculators
            </Button>
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <Calculator className="h-8 w-8" />
              </div>
              <h1 className="text-3xl font-bold text-secondary">
                SSY Calculator
              </h1>
            </div>
          </div>

          <div className="mx-auto max-w-7xl">
            <div className="grid gap-8 lg:grid-cols-2">
              <Card className="border-border bg-card">
                <CardContent className="space-y-6 p-6 md:p-8">
                  <div className="mb-2 flex items-center gap-3">
                    <div className="rounded-xl bg-pink-100 p-3">
                      <Baby className="h-6 w-6 text-pink-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-secondary">
                        SSY Calculator
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Sukanya Samriddhi Yojana
                      </p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <CompactCalculatorRow
                      label="Yearly investment"
                      value={ssyAmount}
                      onChange={setSsyAmount}
                      min={250}
                      max={150000}
                      step={250}
                      prefix="₹"
                      limitLabels={["₹250", "₹1.5 L (Limit)"]}
                      colorClass="text-primary"
                    />

                    <CompactCalculatorRow
                      label="Girl child age"
                      value={ssyAge}
                      onChange={setSsyAge}
                      min={1}
                      max={10}
                      step={1}
                      suffix="Yrs"
                      colorClass="text-pink-600"
                    />

                    <CompactCalculatorRow
                      label="Rate of interest"
                      value={ssyRate}
                      readOnly
                      suffix="%"
                      colorClass="text-pink-600"
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card className="border-border bg-muted/20">
                  <CardContent className="p-8">
                    <div className="mb-6 text-center">
                      <p className="mb-1 text-sm font-medium text-muted-foreground">
                        Estimated Maturity Value
                      </p>
                      <h2 className="text-4xl font-black text-pink-600">
                        {formatCurrency(ssyResult.maturity)}
                      </h2>
                    </div>

                    <div className="mb-6 grid grid-cols-2 gap-4">
                      <div className="h-[180px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={ssyPieData}
                              cx="50%"
                              cy="50%"
                              innerRadius={45}
                              outerRadius={70}
                              dataKey="value"
                            >
                              {ssyPieData.map(e => (
                                <Cell key={e.name} fill={e.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(v: number | undefined) =>
                                v !== undefined ? formatCurrency(v) : ""
                              }
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex flex-col justify-center space-y-2">
                        {ssyPieData.map(item => (
                          <div
                            key={item.name}
                            className="flex items-center gap-2"
                          >
                            <div
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: item.color }}
                            />
                            <span className="text-sm text-muted-foreground">
                              {item.name}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between rounded-xl bg-slate-50 p-4">
                        <span className="text-muted-foreground">
                          Total Invested (15 yrs)
                        </span>
                        <span className="font-bold">
                          {formatCurrency(ssyResult.invested)}
                        </span>
                      </div>
                      <div className="flex justify-between rounded-xl bg-slate-50 p-4">
                        <span className="text-muted-foreground">
                          Total Interest Accrued
                        </span>
                        <span className="font-bold text-green-600">
                          {formatCurrency(ssyResult.interest)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  );
};
